import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { ProjectPnl } from "@/lib/profitability-api";
import { queryKeys } from "@/lib/query";

/**
 * The project's profit-and-loss statement.
 *
 * Its own query rather than a slice of `useProjectDetail`, and `enabled` is the
 * reason: the statement lives behind a tab, and a second round trip to the
 * engine should not be paid for by every visit that never leaves Overview.
 */
export function useProjectPnl(demandId: string, range?: string, enabled = true) {
  return useQuery({
    // The range is part of the key because `range_total` moves with it. A
    // "Last quarter" statement served from cache under "Last 12 months" would
    // be wrong in exactly the column the reader opened the tab for.
    queryKey: [...queryKeys.projectDetail(demandId), "pnl", range ?? "default"],
    queryFn: async (): Promise<ProjectPnl> => {
      const response = await fetch(
        `/api/projects/${encodeURIComponent(demandId)}/pnl${range ? `?range=${range}` : ""}`,
      );
      if (!response.ok) {
        throw await engineError(response, "Could not load this project's P&L");
      }
      return response.json();
    },
    enabled: enabled && demandId.length > 0,
    // Month-end close data, same as the detail query.
    staleTime: 5 * 60 * 1000,
  });
}
